import React, { useState, useEffect, useRef } from 'react'
import { Button, Container, Row, Col, Spinner, Form, Tooltip, OverlayTrigger, ButtonGroup, Dropdown, Alert } from 'react-bootstrap'
import { inventoryHistoryAPI } from '../api/inventoryHistoryAPI'
import { userAPI } from '../api/userAPI'
import { formatDate, formatDateTime, highlightText, formatQuantity, formatUnitCost } from '../utils/commonUtils'
import PaginationComponent from '../components/Pagination'
import StockItemInfo from '../components/StockItemInfo'
import { createColumnToggleDropdown, createFilterDropdown, handleSearchWithDebounce } from '../utils/tableUtils'
import ResizableTable from '../components/ResizableTable'
import DateFilterDropdown from '../components/DateFilterDropdown'
import { usePermissions } from '../contexts/PermissionContext'
import SearchInputWithClear from '../components/SearchInputWithClear'

function InventoryHistoryListScreen() {
    const { hasPermission, loading: permissionsLoading } = usePermissions()
    const [inventoryHistoryItems, setInventoryHistoryItems] = useState([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [searchInputValue, setSearchInputValue] = useState('')
    const [currentPage, setCurrentPage] = useState(1)
    const [columnVisibility, setColumnVisibility] = useState(() => {
        const savedVisibility = localStorage.getItem('inventoryHistoryColumnVisibility');
        return savedVisibility ? JSON.parse(savedVisibility) : {
            inventoryId: true,
            stockItemId: true,
            image: true,
            gtin: true,
            upc: false,
            lot: true,
            cost: true
        };
    });

    const [columnWidths, setColumnWidths] = useState(() => {
        const savedWidths = localStorage.getItem('inventoryHistoryColumnWidths');
        return savedWidths ? JSON.parse(savedWidths) : {
            date: 150,
            user: 150,
            inventoryId: 120,
            stockItemId: 120,
            image: 80,
            gtin: 150,
            upc: 150,
            stockItem: 300,
            action: 120,
            quantity: 100,
            lot: 120,
            cost: 100,
            description: 300,
            reason: 120,
            notes: 200
        };
    });

    const columnLabels = {
        inventoryId: 'Inventory ID',
        stockItemId: 'Stock Item ID',
        image: 'Image',
        gtin: 'GTIN',
        upc: 'UPC',
        lot: 'LOT',
        cost: 'Cost'
    };

    const [totalPages, setTotalPages] = useState(0);
    const debounceTimeoutRef = useRef(null);
    const [historyDateRange, setHistoryDateRange] = useState({
        startDate: null,
        endDate: null
    });
    const [shouldFetchData, setShouldFetchData] = useState(0);
    const [displayedDateRange, setDisplayedDateRange] = useState({ startDate: null, endDate: null });
    const [selectedAction, setSelectedAction] = useState('');
    const [selectedReason, setSelectedReason] = useState('');
    const [selectedUser, setSelectedUser] = useState('');
    const [userMap, setUserMap] = useState({});
    const [pageSize, setPageSize] = useState(PaginationComponent.getCurrentPageSize());

    const actionMap = {
        'receive': 'Receive',
        'delete': 'Delete',
        'amend': 'Edit',
        'move': 'Move',
        'transfer': 'Transfer',
        'dispose': 'Dispose',
        'return': 'Return',
        'issue': 'Issue',
        'auto-adjust': 'Auto-Adjust',
        'customer-return': 'Customer Return',
        'production': 'Production'
    };

    const reasonMap = {
        'quality': 'Quality',
        'expiration': 'Expiration',
        'short-dated': 'Short-Dated',
        'damaged': 'Damaged',
        'recall': 'Recall',
        'depricated': 'Depricated',
        'poor-sales': 'Poor Sales',
        'other': 'Other'
    };

    const handleActionFilterChange = (actionKey) => {
        setSelectedAction(actionKey);
        setCurrentPage(1);
    };

    const handleReasonFilterChange = (reasonKey) => {
        setSelectedReason(reasonKey);
        setCurrentPage(1);
    };

    useEffect(() => {
        localStorage.setItem('inventoryHistoryColumnVisibility', JSON.stringify(columnVisibility));
    }, [columnVisibility]);

    useEffect(() => {
        localStorage.setItem('inventoryHistoryColumnWidths', JSON.stringify(columnWidths));
    }, [columnWidths]);

    useEffect(() => {
        async function fetchHistoryItems() {
            try {
                setLoading(true);
                console.log('Fetching history items...');
                const response = await inventoryHistoryAPI.getAll(
                    currentPage,
                    pageSize,
                    searchTerm,
                    selectedAction,
                    selectedReason,
                    selectedUser,
                    historyDateRange.startDate?.toISOString().split('T')[0],
                    historyDateRange.endDate?.toISOString().split('T')[0]
                );

                const data = response.results;
                
                if (!Array.isArray(data)) {
                    throw new Error('Expected an array of history items');
                }
                setInventoryHistoryItems(data);
                setTotalPages(response.count);
            } catch (error) {
                console.error('Error fetching history items:', error);
            } finally {
                setLoading(false);
            }
        }
        fetchHistoryItems();
    }, [currentPage, searchTerm, selectedAction, selectedReason, selectedUser, shouldFetchData, pageSize]);

    useEffect(() => {
        async function fetchUsers() {
            console.log('Fetching users...');
            try {
                const users = await userAPI.getAll();
                console.log('Users fetched:', users);
                const mapping = users.reduce((acc, user) => {
                    acc[user.id] = user.username;
                    return acc;
                }, {});
                console.log('User mapping created:', mapping);
                setUserMap(mapping);
            } catch (error) {
                console.error('Error fetching users:', error);
            }
        }
        fetchUsers();
    }, []);

    if (permissionsLoading) {
        return (
            <Container className="py-3">
                <div className="text-center">Loading...</div>
            </Container>
        )
    }

    if (!hasPermission('inventory.view')) {
        return (
            <Container className="py-3">
                <Alert variant="danger">
                    You don&apos;t have permission to view inventory history.
                </Alert>
            </Container>
        )
    }

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber)
    }

    const toggleColumnVisibility = (column) => {
        setColumnVisibility(prevState => ({
            ...prevState,
            [column]: !prevState[column]
        }));
    };

    const handleClearSearch = () => {
        setSearchInputValue('');
        setSearchTerm('');
        setCurrentPage(1);
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchInputValue(value);
        handleSearchWithDebounce(
            value,
            setSearchTerm,
            setCurrentPage,
            debounceTimeoutRef
        );
    };

    const handleUserFilterChange = (userId) => {
        setSelectedUser(userId);
        setCurrentPage(1);
    };

    const displayedHistoryItems = inventoryHistoryItems;

    return (
        <Container fluid className="px-4 py-4">
            <Row className="mb-3 align-items-center">
                <Col>
                    <h1>Inventory History</h1>
                </Col>
                <Col>
                    {createColumnToggleDropdown(columnVisibility, columnLabels, toggleColumnVisibility)}
                </Col>
                <Col md={4}>
                    <SearchInputWithClear
                        value={searchInputValue}
                        onChange={handleSearchChange}
                        onClear={handleClearSearch}
                        placeholder="Search by ID, Description, GTIN, UPC, Notes..."
                        autoComplete="off"
                        spellCheck="false"
                    />
                </Col>
            </Row>

            {loading ? (
                <div className="text-center py-4">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                </div>
            ) : (
                <div className="table-responsive">
                    <ResizableTable
                        tableId="inventory_history_table"
                        columns={[
                            {
                                id: 'date',
                                header: () => (
                                    <DateFilterDropdown
                                        dateRange={historyDateRange}
                                        setDateRange={setHistoryDateRange}
                                        displayedDateRange={displayedDateRange}
                                        setDisplayedDateRange={setDisplayedDateRange}
                                        onApply={() => setShouldFetchData(prev => prev + 1)}
                                        dropdownId="history-date-filter-dropdown"
                                    />
                                ),
                                cell: (row) => formatDateTime(row.date)
                            },
                            {
                                id: 'user',
                                header: () => (
                                    <Dropdown onSelect={handleUserFilterChange}>
                                        <Dropdown.Toggle 
                                            variant="light" 
                                            id="user-filter-dropdown"
                                            style={{
                                                fontWeight: 'bold',
                                                padding: '0rem 0.5rem 0.35rem',
                                                height: '25px'
                                            }}
                                        >
                                            {selectedUser ? userMap[selectedUser] : 'User'}
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu>
                                            <Dropdown.Item eventKey="">All Users</Dropdown.Item>
                                            {Object.entries(userMap).map(([id, name]) => (
                                                <Dropdown.Item key={id} eventKey={id}>
                                                    {name}
                                                </Dropdown.Item>
                                            ))}
                                        </Dropdown.Menu>
                                    </Dropdown>
                                ),
                                cell: (row) => row.user_name
                            },
                            ...(columnVisibility.inventoryId ? [{
                                id: 'inventoryId',
                                header: 'Inventory ID',
                                cell: (row) => highlightText(String(row.inventory_item_id).padStart(6, '0'), searchTerm)
                            }] : []),
                            ...(columnVisibility.stockItemId ? [{
                                id: 'stockItemId',
                                header: 'Stock Item ID',
                                cell: (row) => row.stock_item ? highlightText(String(row.stock_item._id).padStart(6, '0'), searchTerm) : ''
                            }] : []),
                            ...(columnVisibility.image ? [{
                                id: 'image',
                                header: 'Image',
                                cell: (row) => row.stock_item?.image ? (
                                    <OverlayTrigger
                                        placement="right"
                                        overlay={
                                            <Tooltip className="stock-item-tooltip">
                                                <StockItemInfo stockItem={row.stock_item} />
                                            </Tooltip>
                                        }
                                    >
                                        <img
                                            src={row.stock_item.image}
                                            alt="Stock Item Thumbnail"
                                            style={{
                                                width: '50px',
                                                height: '50px',
                                                objectFit: 'contain'
                                            }}
                                        />
                                    </OverlayTrigger>
                                ) : null
                            }] : []),
                            ...(columnVisibility.gtin ? [{
                                id: 'gtin',
                                header: 'GTIN',
                                cell: (row) => highlightText(row.stock_item?.gtin || row.stock_item_gtin || '', searchTerm)
                            }] : []),
                            ...(columnVisibility.upc ? [{
                                id: 'upc',
                                header: 'UPC',
                                cell: (row) => row.stock_item ? highlightText(row.stock_item.upc, searchTerm) : ''
                            }] : []),
                            {
                                id: 'stockItem',
                                header: 'Stock Item',
                                cell: (row) => highlightText(row.stock_item?.description || row.stock_item_description || '', searchTerm)
                            },
                            {
                                id: 'action',
                                header: () => (
                                    <Dropdown onSelect={handleActionFilterChange}>
                                        <Dropdown.Toggle 
                                            variant="light" 
                                            id="action-filter-dropdown"
                                            style={{
                                                fontWeight: 'bold',
                                                padding: '0rem 0.5rem 0.35rem',
                                                height: '25px'
                                            }}
                                        >
                                            {selectedAction ? actionMap[selectedAction] : 'Action'}
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu>
                                            <Dropdown.Item eventKey="">All Actions</Dropdown.Item>
                                            {Object.entries(actionMap).map(([key, label]) => (
                                                <Dropdown.Item key={key} eventKey={key}>
                                                    {label}
                                                </Dropdown.Item>
                                            ))}
                                        </Dropdown.Menu>
                                    </Dropdown>
                                ),
                                cell: (row) => row.action
                            },
                            {
                                id: 'quantity',
                                header: 'Quantity',
                                cell: (row) => formatQuantity(row.quantity)
                            },
                            ...(columnVisibility.lot ? [{
                                id: 'lot',
                                header: 'LOT',
                                cell: (row) => highlightText(row.lot, searchTerm) || ''
                            }] : []),
                            ...(columnVisibility.cost ? [{
                                id: 'cost',
                                header: 'Cost',
                                cell: (row) => row.cost ? formatUnitCost(row.cost) : ''
                            }] : []),
                            {
                                id: 'description',
                                header: 'Description',
                                cell: (row) => highlightText(row.description, searchTerm)
                            },
                            {
                                id: 'reason',
                                header: () => (
                                    <Dropdown onSelect={handleReasonFilterChange}>
                                        <Dropdown.Toggle 
                                            variant="light" 
                                            id="reason-filter-dropdown"
                                            style={{
                                                fontWeight: 'bold',
                                                padding: '0rem 0.5rem 0.35rem',
                                                height: '25px'
                                            }}
                                        >
                                            {selectedReason ? reasonMap[selectedReason] : 'Reason'}
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu>
                                            <Dropdown.Item eventKey="">All Reasons</Dropdown.Item>
                                            {Object.entries(reasonMap).map(([key, label]) => (
                                                <Dropdown.Item key={key} eventKey={key}>
                                                    {label}
                                                </Dropdown.Item>
                                            ))}
                                        </Dropdown.Menu>
                                    </Dropdown>
                                ),
                                cell: (row) => row.reason
                            },
                            {
                                id: 'notes',
                                header: 'Notes',
                                cell: (row) => highlightText(row.notes, searchTerm)
                            }
                        ]}
                        data={displayedHistoryItems}
                        initialColumnWidths={columnWidths}
                        striped
                        hover
                    />
                    <PaginationComponent
                        currentPage={currentPage}
                        totalItems={totalPages}
                        onPageChange={handlePageChange}
                        onItemsPerPageChange={setPageSize}
                    />
                </div>
            )}
        </Container>
    )
}

export default InventoryHistoryListScreen